/**
 * The pure kernel of a bot's turn, extracted from `scenes/BotDriver.ts`: pick
 * the seat's own-turn action, apply it, and collect every bot seat's answer to
 * an open claim window. `BotDriver` keeps the timers, the stale-timer guards
 * and the scene hooks; nothing here knows about either.
 *
 * Pure — no canvas, no DOM, no timers, and no notion of a single "local" seat,
 * so the same decisions drive solo free play (one human, three bots) and a
 * networked table (zero to four humans) alike.
 */

import { canTsumo, declareTsumo, discardTile, type MahjongState } from '../game/GameState.js';
import type { Claim, SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { Rng } from '../utils/prng.js';
import { applyOwnTurnKong } from '../scenes/BotKong.js';
import type { AiStrategy, KongDecision } from './types.js';

/** What a bot decided to do on its own turn — chosen and applied separately so
 *  the caller can log/broadcast around the transition. */
export type BotTurnAction =
  | { readonly kind: 'tsumo' }
  | { readonly kind: 'kong'; readonly kong: KongDecision }
  | { readonly kind: 'discard'; readonly tileId: number };

/** Which seats a claim window may be answered for on the bots' behalf. */
export type BotSeatPredicate = (seat: Seat) => boolean;

/**
 * The bot's own-turn decision in the engine's precedence order: win, else kong,
 * else discard. The caller guarantees `state` is `awaiting-discard` on `seat`.
 */
export function chooseBotTurnAction(
  state: MahjongState,
  seat: Seat,
  strategy: AiStrategy,
  rng: Rng,
): BotTurnAction {
  const hand = state.hands[seat] ?? [];
  const meldCount = (state.melds[seat] ?? []).length;
  if (canTsumo(state) && strategy.chooseTsumo(hand, meldCount)) return { kind: 'tsumo' };
  // Winning (checked above) always beats konging.
  const kong = strategy.chooseKong(seat, state);
  if (kong !== null) return { kind: 'kong', kong };
  return { kind: 'discard', tileId: strategy.chooseDiscard(hand, rng).id };
}

/** Maps a chosen `BotTurnAction` onto the engine transition that performs it. */
export function applyBotTurnAction(
  state: MahjongState,
  seat: Seat,
  action: BotTurnAction,
): MahjongState {
  switch (action.kind) {
    case 'tsumo':
      return declareTsumo(state, seat);
    case 'kong':
      return applyOwnTurnKong(state, seat, action.kong);
    case 'discard':
      return discardTile(state, seat, action.tileId);
  }
}

/**
 * Every bot seat's claim against the open discard, in `seatClaims` order.
 * Seats `isBotSeat` rejects are skipped entirely — a human's claim window is
 * answered by real input, and a table holds zero to four of them. Cross-seat
 * priority stays the caller's job (`resolveClaims`).
 */
export function collectBotClaims(
  seatClaims: readonly SeatClaims[],
  state: MahjongState,
  strategy: AiStrategy,
  isBotSeat: BotSeatPredicate,
): Claim[] {
  const botClaims: Claim[] = [];
  for (const claims of seatClaims) {
    if (!isBotSeat(claims.seat)) continue;
    const choice = strategy.chooseClaim(claims.seat, state, claims);
    if (choice !== null) botClaims.push(choice);
  }
  return botClaims;
}
